import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { CurrencySelector } from '../components/CurrencySelector';
import { PeriodFilter, type DateRange } from '../components/PeriodFilter';
import { SalesFilterBar } from '../components/SalesFilterBar';
import { SalesChart } from '../components/SalesChart';
import { SalesTable } from '../components/SalesTable';
import { StatCard } from '../components/StatCard';
import { fetchAdminOverview, fetchAdminSales, type AdminOverview, type AdminSaleRow } from '../lib/api';
import { formatCurrency, pickCurrency } from '../lib/format';
import { COMMISSION_GENERATING_STATUSES } from '../lib/statusLabels';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const ADMIN_NAV = [
  { to: '/admin', label: 'Visão geral' },
  { to: '/admin/afiliados', label: 'Afiliados' },
];

export function AdminDashboardPage() {
  const { signOut } = useAdminAuth();
  const [range, setRange] = useState<DateRange>({});
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [hideCanceled, setHideCanceled] = useState(false);
  const [page, setPage] = useState(1);
  const [currency, setCurrency] = useState('BRL');

  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [sales, setSales] = useState<{ rows: AdminSaleRow[]; hasMore: boolean }>({ rows: [], hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [range.from, range.to, status, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchAdminOverview(range),
      fetchAdminSales({ from: range.from, to: range.to, status: status || undefined, search: search || undefined, page }),
    ])
      .then(([overviewResult, salesResult]) => {
        if (cancelled) return;
        setOverview(overviewResult);
        setSales(salesResult);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Erro ao carregar os dados.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range, status, search, page]);

  // Mesmo critério de InfluencerDashboardPage.tsx: a lista de moedas vem só
  // de quem tem venda de verdade no período (overview.totals.byCurrency,
  // já ordenado com BRL primeiro pelo Worker) — nunca a lista inteira de
  // moedas ISO.
  const availableCurrencies = overview?.totals.byCurrency.map((entry) => entry.currency) ?? [];
  useEffect(() => {
    if (availableCurrencies.length === 0) return;
    if (!availableCurrencies.includes(currency)) {
      setCurrency(availableCurrencies[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCurrencies.join(',')]);

  const totalsBucket = pickCurrency(overview?.totals.byCurrency, currency);
  const chartSeries = overview?.seriesByCurrency.find((entry) => entry.currency === currency)?.series ?? [];
  const visibleSalesRows = hideCanceled ? sales.rows.filter((row) => COMMISSION_GENERATING_STATUSES.has(row.status)) : sales.rows;

  return (
    <Layout nav={ADMIN_NAV} onSignOut={() => signOut()}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight">Visão geral</h1>
        <CurrencySelector currencies={availableCurrencies} value={currency} onChange={setCurrency} />
      </div>
      <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">Todas as vendas e afiliados do CA Render.</p>

      <div className="mt-6">
        <PeriodFilter onChange={setRange} />
      </div>

      {error ? <p className="mt-4 text-sm text-error">{error}</p> : null}

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Vendas totais" value={totalsBucket ? String(totalsBucket.salesCount) : 'N/D'} />
        <StatCard label="Faturamento total" value={totalsBucket ? formatCurrency(totalsBucket.revenue, currency) : 'N/D'} />
        <StatCard label="Comissões dos afiliados" value={totalsBucket ? formatCurrency(totalsBucket.commission, currency) : 'N/D'} />
      </div>

      <div className="mt-6">
        <SalesChart series={chartSeries} currency={currency} />
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold tracking-tight">Ranking de afiliados</h2>
      <div className="card mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--border))] text-left text-xs uppercase tracking-wide text-[rgb(var(--text-muted))]">
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">Afiliado</th>
              <th className="px-4 py-3 font-medium text-right">Vendas</th>
              <th className="px-4 py-3 font-medium text-right">Faturamento</th>
              <th className="px-4 py-3 font-medium text-right">Comissão</th>
              <th className="px-4 py-3 font-medium text-right">Reembolsos</th>
            </tr>
          </thead>
          <tbody>
            {(overview?.ranking ?? []).map((entry, index) => {
              // Ranking segue a mesma moeda selecionada no topo da página —
              // um afiliado sem venda nenhuma na moeda escolhida mostra 0,
              // nunca o valor de outra moeda (mesmo critério dos cards
              // acima e do gráfico).
              const bucket = pickCurrency(entry.byCurrency, currency);
              return (
                <tr key={entry.email ?? entry.name} className="border-b border-[rgb(var(--border))] last:border-0">
                  <td className="px-4 py-3 text-[rgb(var(--text-muted))]">{index + 1}</td>
                  <td className="px-4 py-3 font-medium">{entry.name}</td>
                  <td className="px-4 py-3 text-right">{bucket ? bucket.salesCount : 0}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bucket ? bucket.revenue : 0, currency)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bucket ? bucket.commission : 0, currency)}</td>
                  <td className="px-4 py-3 text-right">{entry.refundsCount}</td>
                </tr>
              );
            })}
            {overview && overview.ranking.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[rgb(var(--text-muted))]">
                  Nenhuma venda com afiliado atribuído neste período.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold tracking-tight">Vendas por origem</h2>
      <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">
        Rastreamento de origem da Hotmart (parâmetro src/sck no link), pra medir tráfego pago sem precisar de cupom.
      </p>
      <div className="card mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--border))] text-left text-xs uppercase tracking-wide text-[rgb(var(--text-muted))]">
              <th className="px-4 py-3 font-medium">Origem</th>
              <th className="px-4 py-3 font-medium text-right">Vendas</th>
              <th className="px-4 py-3 font-medium text-right">Faturamento</th>
              <th className="px-4 py-3 font-medium text-right">Comissão</th>
            </tr>
          </thead>
          <tbody>
            {(overview?.bySource ?? []).map((entry) => {
              // Mesmo critério do ranking de afiliados acima: segue a
              // moeda selecionada no topo da página, 0 se não houver
              // venda daquela origem na moeda escolhida.
              const bucket = pickCurrency(entry.byCurrency, currency);
              return (
                <tr key={entry.sourceCode} className="border-b border-[rgb(var(--border))] last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{entry.sourceCode}</td>
                  <td className="px-4 py-3 text-right">{bucket ? bucket.salesCount : 0}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bucket ? bucket.revenue : 0, currency)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bucket ? bucket.commission : 0, currency)}</td>
                </tr>
              );
            })}
            {overview && overview.bySource.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-[rgb(var(--text-muted))]">
                  Nenhuma venda com rastreamento de origem neste período.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-display text-lg font-semibold tracking-tight">Vendas de afiliados</h2>
        <SalesFilterBar
          status={status}
          search={search}
          onStatusChange={setStatus}
          onSearchChange={setSearch}
          hideCanceled={hideCanceled}
          onHideCanceledChange={setHideCanceled}
        />
      </div>

      <div className="mt-3">
        <SalesTable
          rows={visibleSalesRows}
          loading={loading}
          page={page}
          hasMore={sales.hasMore}
          onPageChange={setPage}
          affiliateColumn={(row) => (row as AdminSaleRow).affiliateName ?? 'Venda direta'}
        />
      </div>
    </Layout>
  );
}
